import dayjs, { Dayjs } from 'dayjs';
import { getAxiosInstance } from '../../react/utils/networkUtil';
import { AppSuccess } from '../models/AppSuccess';
import { BBox } from 'geojson';
import { RemoteImageAlert_Base } from '../models/RemoteImageAlert';

/**
 * Đăng ký/ Huỷ đăng ký nhận tin ảnh vệ tinh mới
 * @returns
 */
export type RegisterRemoteImageAlert_Request = {
  gid_1: string;
  gid_2?: string;
  gid_3?: string;

  enabled: boolean;
};
export const remoteRemoteImageAlert_Register = async (
  request: RegisterRemoteImageAlert_Request,
): Promise<AppSuccess<any>> => {
  const response = await getAxiosInstance().post('api/remote-image-alert-ext/register-alert', request);
  return response.data;
};

/**
 * Get list ảnh vệ tinh
 */
export type RemoteImageAlert_GetList_Request = {
  gid_1?: string;
  gid_2?: string;
  gid_3?: string;

  start_date: Dayjs;
  end_date: Dayjs;
  collection_name?: string;
  cloud_cover?: number;
  point?: GeoJSON.Point;
};
export type RemoteImageAlert_GetList_RequestParams = {
  gid_1: string;
  gid_2?: string;
  gid_3?: string;

  start_date: string;
  end_date: string;
  collection_name?: string;
  cloud_cover?: number;
  point_lat?: number;
  point_lon?: number;
};
export type RemoteImageAlert_GetList_ResponseItem = {
  id: string;
  thumbUrl: string;
  bbox: any;
  wmsUrl: string;
  imageDescription: any;
};
export const remoteRemoteImageAlert_GetList = async (
  request: RemoteImageAlert_GetList_Request,
): Promise<RemoteImageAlert_GetList_ResponseItem[]> => {
  const params: RemoteImageAlert_GetList_RequestParams = {
    gid_1: request.gid_1 || '',
    gid_2: request.gid_2 || '',
    gid_3: request.gid_3 || '',
    start_date: request.start_date.format('YYYY-MM-DD'),
    end_date: request.end_date.format('YYYY-MM-DD'),
    collection_name: request.collection_name,
    cloud_cover: request.cloud_cover,
    point_lat: request.point?.coordinates[1],
    point_lon: request.point?.coordinates[0],
  };

  const response = await getAxiosInstance().get('api/remote-image-alert-ext', {
    params: params,
  });
  return response.data;
};
// End: Get list ảnh vệ tinh

/**
 * Refresh gee wms
 */
export const remoteRemoteImageAlert_RefreshWMS = async (id: string): Promise<{ wms: string }> => {
  const response = await getAxiosInstance().get('api/remote-image-alert-ext/refresh-wms', {
    params: {
      id: id,
    },
  });
  return response.data;
};
// End: Refresh gee wms

// remote_RemoteImageAlert_getInterestedArea
export type RemoteImageAlert_GetInterestedArea_ResponseItem = RemoteImageAlert_Base & {
  gid_1_name: string;
  gid_2_name: string;
  gid_3_name: string;
}
export const remote_RemoteImageAlert_getInterestedArea = async (): Promise<Array<RemoteImageAlert_GetInterestedArea_ResponseItem>> => {
  const response = await getAxiosInstance().get('api/remote-image-alert-ext/my-interested-area');
  return response.data;
};
// End: remote_RemoteImageAlert_getInterestedArea

// remote_GetListPlanetMosaic
export type PlanetMosaicItem = {
  id: string;
  title: string;
  description: string;
  url: string;
  year: number;
  quarter: number;
  month: number;
  title_vi: string;
}

export enum PlanetMosaicType {
  NEWEST = 'newest',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  NORMALIZED = 'normalized',
}
export type RemoteImageAlert_GetListPlanetMosaic_Request = {
  type?: PlanetMosaicType;
  year?: number;
  quarter?: number;
  month?: number;
};
export const remote_GetListPlanetMosaic = async (request: RemoteImageAlert_GetListPlanetMosaic_Request): Promise<PlanetMosaicItem[]> => {
  return (await getAxiosInstance().get('/api/remote-image-alert-ext/planet-mosaic', {
    params: request,
  })).data;
}
// END: remote_GetListPlanetMosaic

// remote_GetDownloadLink
export const remote_GetDownloadLink = async (imageId: string): Promise<{ link: string }> => {
  return (await getAxiosInstance().get('/api/remote-image-alert-ext/download-link', {
    params: {
      imageId: imageId,
    },
  })).data;
}
// END: remote_GetDownloadLink

// remote_RemoteImageAlert_getThumbnail
export const remote_RemoteImageAlert_getThumbnail = async (id: string): Promise<Blob> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/thumbnail', {
    params: { id },
    responseType: 'blob',
  });
  return response.data;
}
// END: remote_RemoteImageAlert_getThumbnail

// Landsat APIs
export const remoteRemoteImageAlert_GetLandsatList = async (
  request: RemoteImageAlert_GetList_Request,
): Promise<RemoteImageAlert_GetList_ResponseItem[]> => {
  const params: RemoteImageAlert_GetList_RequestParams = {
    gid_1: request.gid_1 || '',
    gid_2: request.gid_2 || '',
    gid_3: request.gid_3 || '',
    start_date: request.start_date.format('YYYY-MM-DD'),
    end_date: request.end_date.format('YYYY-MM-DD'),
    collection_name: request.collection_name,
    cloud_cover: request.cloud_cover,
    point_lat: request.point?.coordinates[1],
    point_lon: request.point?.coordinates[0],
  };

  const response = await getAxiosInstance().get('api/remote-image-alert-ext/landsat/getPage', {
    params: params,
  });
  return response.data;
};

export const remoteRemoteImageAlert_RefreshLandsatWMS = async (id: string): Promise<{ wms: string }> => {
  const response = await getAxiosInstance().get('api/remote-image-alert-ext/landsat/refresh-wms', {
    params: {
      id: id,
    },
  });
  return response.data;
};

export const remote_RemoteImageAlert_getLandsatThumbnail = async (id: string): Promise<Blob> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/landsat/thumbnail', {
    params: { id },
    responseType: 'blob',
  });
  return response.data;
}
// END: Landsat APIs

// S2 Mosaic Quarterly APIs (Copernicus Sentinel Hub)
export type S2MosaicQuarterly_Item = {
  id: string;
  year: number;
  quarter: number;
  start_date: string;
  end_date: string;
  title_vi: string;
  title_en?: string;
  thumbUrl: string;
  tileUrl: string;
  bbox: any;
  cloud_percentage: number;
  imageDescription: any;
};
export type CopernicusS2_Mosaic_Item = S2MosaicQuarterly_Item;

export type S2MosaicQuarterly_GetPage_Request = {
  year?: number;
  quarter?: number;
  page?: number;
  pageSize?: number;
};
export type CopernicusS2_GetPage_Request = S2MosaicQuarterly_GetPage_Request;

export const remote_S2MosaicQuarterly_GetPage = async (
  request?: S2MosaicQuarterly_GetPage_Request
): Promise<S2MosaicQuarterly_Item[]> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/sentinel-2-mosaic-quarterly/getPage', {
    params: request,
  });
  return response.data;
};
export const remote_CopernicusS2_GetPage = remote_S2MosaicQuarterly_GetPage;

export const remote_S2MosaicQuarterly_GetThumbnail = async (id: string): Promise<Blob> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/sentinel-2-mosaic-quarterly/thumbnail', {
    params: { id },
    responseType: 'blob',
  });
  return response.data;
};
export const remote_CopernicusS2_GetThumbnail = remote_S2MosaicQuarterly_GetThumbnail;
// END: S2 Mosaic Quarterly APIs

// START: Landsat Bi-monthly Mosaic APIs
export type LandsatMosaic_Item = {
  id: string;
  year: number;
  period: number;
  start_date: string;
  end_date: string;
  title_vi: string;
  title_en?: string;
  thumbUrl: string;
  tileUrl: string;
  bbox?: any;
  cloud_percentage: number;
  imageDescription: {
    start_date: string;
    end_date: string;
    provider: string;
  };
};

export type LandsatMosaic_GetPage_Request = {
  year?: number;
  period?: number;
  page?: number;
  pageSize?: number;
};

export const remote_LandsatMosaic_GetPage = async (
  request?: LandsatMosaic_GetPage_Request
): Promise<LandsatMosaic_Item[]> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/landsat-mosaic-bimonthly/getPage', {
    params: request,
  });
  return response.data;
};

export const remote_LandsatMosaic_GetThumbnail = async (id: string): Promise<Blob> => {
  const response = await getAxiosInstance().get('/api/remote-image-alert-ext/landsat-mosaic-bimonthly/thumbnail', {
    params: { id },
    responseType: 'blob',
  });
  return response.data;
};
// END: Landsat Bi-monthly Mosaic APIs

export const RemoteImageAlertAPIs = {
  remoteRemoteImageAlert_Register,
  remoteRemoteImageAlert_GetList,
  remoteRemoteImageAlert_RefreshWMS,
  remote_RemoteImageAlert_getInterestedArea,
  remote_GetListPlanetMosaic,
  remote_GetDownloadLink,
  remote_RemoteImageAlert_getThumbnail,
  remoteRemoteImageAlert_GetLandsatList,
  remoteRemoteImageAlert_RefreshLandsatWMS,
  remote_RemoteImageAlert_getLandsatThumbnail,
  remote_S2MosaicQuarterly_GetPage,
  remote_S2MosaicQuarterly_GetThumbnail,
  remote_CopernicusS2_GetPage,
  remote_CopernicusS2_GetThumbnail,
  remote_LandsatMosaic_GetPage,
  remote_LandsatMosaic_GetThumbnail,
};


